// ============================================================================
// CHECKOUT TIME BANNER
// Shows guest checkout time and when cleaner can begin cleaning
// ============================================================================

import { Clock, DoorOpen, CheckCircle2, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "@/hooks/useTranslation";
import { parseTimeToDate, hasCheckoutTimePassed } from "@/lib/effectiveTimes";

interface CheckoutTimeBannerProps {
  checkoutTime: string | null;
  earlyCheckoutDetected?: boolean | null;
  unitEmptyOnArrival?: boolean | null;
  sticky?: boolean;
}

const CheckoutTimeBanner = ({
  checkoutTime,
  earlyCheckoutDetected,
  unitEmptyOnArrival,
  sticky = false,
}: CheckoutTimeBannerProps) => {
  const { tDynamic, isRtl } = useTranslation();

  const stickyClasses = sticky ? "sticky top-[60px] z-20" : "";

  // Reservation / PMS signal: guest left before scheduled checkout
  if (earlyCheckoutDetected) {
    return (
      <Card className={`mb-4 border-green-200 bg-green-50 ${stickyClasses}`} dir={isRtl ? "rtl" : "ltr"}>
        <CardContent className="py-3">
          <div className="flex items-center gap-3">
            <DoorOpen className="w-5 h-5 text-green-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800">
                {tDynamic("Early checkout detected")}
              </p>
              <p className="text-xs text-green-700">
                {tDynamic("You may begin cleaning now")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no checkout time available
  if (!checkoutTime) {
    return (
      <Card className={`mb-4 border-muted bg-muted/30 ${stickyClasses}`} dir={isRtl ? "rtl" : "ltr"}>
        <CardContent className="py-3">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">
                {tDynamic("Guest Checkout Time")}
              </p>
              <p className="text-xs text-muted-foreground">
                {tDynamic("Checkout time not available. Contact the owner.")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const checkoutPassed = hasCheckoutTimePassed(checkoutTime);

  // Checkout time passed, or cleaner confirmed unit was empty (may begin before clock checkout)
  if (checkoutPassed === true || unitEmptyOnArrival) {
    const usePassedCopy = checkoutPassed === true;
    return (
      <Card className={`mb-4 border-green-200 bg-green-50 ${stickyClasses}`} dir={isRtl ? "rtl" : "ltr"}>
        <CardContent className="py-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800">
                {tDynamic("Guest checks out at:")} {checkoutTime}
              </p>
              <p className="text-xs text-green-700">
                {usePassedCopy
                  ? tDynamic("Checkout time has passed. You may begin cleaning.")
                  : tDynamic("You may begin cleaning now")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Checkout time is in the future - yellow/orange banner
  if (checkoutPassed === false) {
    return (
      <Card className={`mb-4 border-amber-200 bg-amber-50 ${stickyClasses}`} dir={isRtl ? "rtl" : "ltr"}>
        <CardContent className="py-3">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {tDynamic("Guest checks out at:")} {checkoutTime}
              </p>
              <p className="text-xs text-amber-700">
                {tDynamic("Please wait until after checkout to begin cleaning.")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Could not parse time (e.g., "Flexible") - show info banner
  return (
    <Card className={`mb-4 border-muted bg-muted/30 ${stickyClasses}`} dir={isRtl ? "rtl" : "ltr"}>
      <CardContent className="py-3">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-muted-foreground shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">
              {tDynamic("Guest Checkout Time:")} {checkoutTime}
            </p>
            <p className="text-xs text-muted-foreground">
              {tDynamic("Contact owner to confirm exact checkout time.")}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CheckoutTimeBanner;
